import React, { useEffect, useState } from "react";
import MetaTags from "react-meta-tags";
import Fade from "react-reveal/Fade";

const JOBS_S3_URL =
  "https://datakraken-assets.s3.eu-west-2.amazonaws.com/datakraken-website-jobs/jobs.json";
const JOBS_FALLBACK_URL =
  "https://uectqrt3xze5oxhn234ltjrzhu0qqhtt.lambda-url.eu-west-2.on.aws/";

const Career = () => {
  const [jobs, setJobs] = useState(/** @type {any[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadJobs = async () => {
      try {
        setLoading(true);
        setError(null);

        const fetchJobsJson = async (url) => {
          const res = await fetch(url);
          if (!res.ok) {
            throw new Error(`Failed to load jobs (${res.status})`);
          }
          return res.json();
        };

        let data;
        try {
          data = await fetchJobsJson(JOBS_S3_URL);
        } catch {
          data = await fetchJobsJson(JOBS_FALLBACK_URL);
        }

        if (isMounted) setJobs(Array.isArray(data) ? data : []);
      } catch (e) {
        if (isMounted) setError(e?.message || "Failed to load jobs");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadJobs();
    return () => {
      isMounted = false;
    };
  }, []);

  const HtmlBlock = ({ html }) => {
    if (!html) return null;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const applyMailto = (jobTitle) => {
    const subject = `Application - ${jobTitle}`;
    const body = `Hi Data Kraken,\n\nI'm applying for the ${jobTitle} role. Please find my CV attached.\n\nThanks,\n`;

    return `mailto:admin@datakraken.net?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const noJobs = !loading && !error && jobs.length === 0;

  return (
    <section className="privacy-policy" >
      <MetaTags>
        <title>Careers - Data Kraken</title>
        <meta
          name="description"
          content="Career at Data Kraken"
        />
      </MetaTags>

      <Fade duration={2000}>
        <div className="container">
          <div className="row">
            <div
              className={`pb-5 ${
                noJobs
                  ? "col-12 d-flex justify-content-center"
                  : "col-12 col-lg-9"
              }`}
            >
              {loading ? (
                <p>Loading roles...</p>
              ) : error ? (
                <p>{error}</p>
              ) : jobs.length > 0 ? (
                <>
                  <h2 className="pb-8">
                    <strong>Careers</strong>
                  </h2>
                  <p className="pb-4">
                    Explore the current opportunities below and apply for
                    the role that fits you best.
                  </p>
                  {jobs.map((job, idx) => (
                    <div
                      key={job.title || idx}
                      className="career--job"
                      style={{
                        borderRadius: 16,
                        padding: "1.75rem 1.5rem",
                        marginBottom: "1.75rem",
                        border: "1px solid rgba(0, 0, 0, 0.06)",
                        background:
                          "linear-gradient(180deg, rgba(225,34,45,0.09) 0%, rgba(255,255,255,1) 160%)",
                        boxShadow: "0 14px 30px rgba(0,0,0,0.06)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: -60,
                          right: -60,
                          width: 170,
                          height: 170,
                          borderRadius: "50%",
                          background:
                            "radial-gradient(circle at center, rgba(225,34,45,0.22) 0%, rgba(225,34,45,0) 65%)",
                          pointerEvents: "none",
                        }}
                      />

                      <div style={{ position: "relative" }}>
                        <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
                          {job.title}
                        </h2>

                        <div style={{ marginTop: "0.55rem" }}>
                          <p style={{ margin: "0.15rem 0 0 0" }}>
                            <strong>Location:</strong> {job.location}
                          </p>
                          <p style={{ margin: "0.15rem 0 0 0" }}>
                            <strong>Salary:</strong> {job.salary}
                          </p>
                        </div>

                        <div className="dk--divider" style={{ margin: "1.25rem auto" }} />

                        <h4 className="pt-0">About</h4>
                        <HtmlBlock html={job.about} />

                        <h4 className="pt-4">Location & Working Pattern</h4>
                        <HtmlBlock
                          html={job["Location_&_Working_Pattern"]}
                        />

                        <h4 className="pt-4">Our Tech Stack</h4>
                        <HtmlBlock html={job["Our_Tech_Stack"]} />

                        <h4 className="pt-4">About the Role</h4>
                        <HtmlBlock html={job["About_the_Role"]} />

                        <h4 className="pt-4">About You</h4>
                        <HtmlBlock html={job["About_You"]} />

                        <h4 className="pt-4">Right to Work</h4>
                        {job["Right_to_Work"] ? (
                          <p><HtmlBlock html={job["Right_to_Work"]} /></p>
                        ) : null}

                        <h4 className="pt-4">Company Benefits</h4>
                        <HtmlBlock html={job["Company_Benefits"]} />

                        {job.footer ? (
                          <>
                            <h4 className="pt-4">Why apply</h4>
                            <HtmlBlock html={job.footer} />
                          </>
                        ) : null}

                        {job.extra_info ? (
                          <>
                            <HtmlBlock html={job.extra_info} />
                          </>
                        ) : null}

                        <p className="pt-4">
                          <a
                            href={applyMailto(job.title)}
                            className="btn btn--red"
                          >
                            Apply for {job.title}
                          </a>
                        </p>

                        <p className="pb-5">
                          If you think you’d be a great fit for the Data
                          Kraken team, please apply through the button above
                          or contact us at{" "}
                          <a href="mailto:admin@datakraken.net">
                            admin@datakraken.net
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div
                    className="career--empty"
                    style={{
                      borderRadius: 16,
                      padding: "3rem 1.5rem",
                      maxWidth: 620,
                      width: "100%",
                      margin: "0 auto 1.75rem",
                      border: "1px solid rgba(0, 0, 0, 0.06)",
                      background:
                        "linear-gradient(180deg, rgba(225,34,45,0.09) 0%, rgba(255,255,255,1) 160%)",
                      boxShadow: "0 14px 30px rgba(0,0,0,0.06)",
                      position: "relative",
                      overflow: "hidden",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: -60,
                        right: -60,
                        width: 170,
                        height: 170,
                        borderRadius: "50%",
                        background:
                          "radial-gradient(circle at center, rgba(225,34,45,0.22) 0%, rgba(225,34,45,0) 65%)",
                        pointerEvents: "none",
                      }}
                    />

                    <div style={{ position: "relative" }}>
                      <div
                        aria-hidden="true"
                        style={{
                          width: 84,
                          height: 84,
                          margin: "0 auto 1.5rem",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(225,34,45,0.10)",
                        }}
                      >
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#e1222d"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="2" y="7" width="20" height="14" rx="2" />
                          <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <path d="M2 13h20" />
                        </svg>
                      </div>

                      <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
                        No open roles at the moment
                      </h2>

                      <p
                        style={{
                          margin: "0.75rem auto 0",
                          maxWidth: 520,
                          color: "rgba(0,0,0,0.6)",
                        }}
                      >
                        We’re not actively hiring right now, but we’re always
                        keen to meet talented people. Send us your CV and we’ll
                        get in touch when a role that fits you opens up.
                      </p>

                      <p className="pt-4" style={{ marginBottom: 0 }}>
                        <a
                          href={applyMailto("Future Opportunities")}
                          className="btn btn--red"
                        >
                          Send us your CV
                        </a>
                      </p>

                      <p
                        className="pt-4"
                        style={{ marginBottom: 0, color: "rgba(0,0,0,0.6)" }}
                      >
                        Or reach out directly at{" "}
                        <a href="mailto:admin@datakraken.net">
                          admin@datakraken.net
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Fade>
    </section>
  );
};

export default Career;
